/**
 * Main public site navigation configuration.
 * Drives both desktop mega menu and mobile nav.
 *
 * Aliases: nav config, navigation menu, site nav, header nav
 */

import { linkOptions } from "@tanstack/react-router";
import {
  Compass,
  Sparkles,
  BookMarked,
  BookOpen,
  BrainCircuit,
  Briefcase,
  Gauge,
  Heart,
  LayoutGrid,
  ListChecks,
  MessageSquareQuote,
  Rocket,
  Route,
  Wrench,
  DollarSign,
  Lightbulb,
  Building2,
  GraduationCap,
  Layers,
  Target,
  type LucideIcon,
} from "lucide-react";

export type NavLink = {
  label: string;
  description?: string;
  icon?: LucideIcon;
  to: string;
  params?: Record<string, string>;
};

export type NavGroup = {
  title: string;
  links: NavLink[];
};

export type NavItemConfig =
  | {
      type: "link";
      label: string;
      to: string;
      params?: Record<string, string>;
    }
  | {
      type: "mega";
      label: string;
      groups: NavGroup[];
      featured?: {
        title: string;
        description: string;
        to: string;
        params?: Record<string, string>;
        ctaLabel: string;
      };
    };

// Each linkOptions() call validates the route against the route tree at compile time.
// If a route is renamed or removed, TypeScript will flag it here.
const coaches = linkOptions({ to: "/coaches" });
const discovery = linkOptions({ to: "/discovery" });
const wheelOfLife = linkOptions({ to: "/wheel-of-life" });
const articles = linkOptions({ to: "/articles" });
const stories = linkOptions({ to: "/stories" });
const coachingFor = linkOptions({ to: "/coaching-for" });
const howItWorks = linkOptions({ to: "/how-it-works" });
const whatWeOffer = linkOptions({ to: "/what-we-offer" });
const about = linkOptions({ to: "/about" });
const readinessScore = linkOptions({ to: "/6-figure-readiness-score" });
const pageCoach = linkOptions({
  to: "/page/$name",
  params: { name: "coach" },
});
const pageNova = linkOptions({
  to: "/page/$name",
  params: { name: "nova" },
});
const pageCoachingOs = linkOptions({
  to: "/page/$name",
  params: { name: "coaching-os" },
});
const pageCoachBuilder = linkOptions({
  to: "/page/$name",
  params: { name: "coach-business-builder" },
});
const pagePricing = linkOptions({
  to: "/page/$name",
  params: { name: "pricing" },
});
const pagePartner = linkOptions({
  to: "/page/$name",
  params: { name: "partner" },
});
const ccos = linkOptions({ to: "/organizations" });
const coachingIntelligence = linkOptions({ to: "/coaching-intelligence" });
const frameworks = linkOptions({ to: "/frameworks" });
const trainingPrograms = linkOptions({ to: "/training-programs" });
const coachingBusiness = linkOptions({ to: "/coaching-business" });
const playbooks = linkOptions({ to: "/coaching-business/playbooks" });

export const navConfig: NavItemConfig[] = [
  {
    type: "mega",
    label: "Find a Coach",
    groups: [
      {
        title: "Find Your Coach",
        links: [
          {
            label: "Get Matched",
            ...discovery,
            description: "Get matched in minutes with AI",
            icon: Sparkles,
          },
          {
            label: "Browse Coaches",
            ...coaches,
            description: "Explore by category, city, or certification",
            icon: Compass,
          },
          {
            label: "Wheel of Life",
            ...wheelOfLife,
            description: "Free 8-area self-assessment — no signup",
            icon: Target,
          },
        ],
      },
      {
        title: "Start Here",
        links: [
          {
            label: "How It Works",
            ...howItWorks,
            description: "From your first visit to your first session",
            icon: Route,
          },
          {
            label: "What We Offer",
            ...whatWeOffer,
            description: "One platform, both sides of coaching",
            icon: LayoutGrid,
          },
          {
            label: "About Us",
            ...about,
            description: "Who we are and why we built this",
            icon: Heart,
          },
        ],
      },
    ],
    featured: {
      title: "Find your dream coach",
      description:
        "Answer a few questions and let our AI match you with the perfect coach — in under 2 minutes.",
      ...discovery,
      ctaLabel: "Start free matching",
    },
  },
  {
    type: "mega",
    label: "Guidance",
    groups: [
      {
        title: "For Your Situation",
        links: [
          {
            label: "What are you facing?",
            ...coachingFor,
            description:
              "Advice, frameworks, and articles for what you're facing",
            icon: Lightbulb,
          },
          {
            label: "Articles",
            ...articles,
            description: "Expert-written guides from credentialed coaches",
            icon: BookOpen,
          },
          {
            label: "Success Stories",
            ...stories,
            description: "How real clients changed with a coach",
            icon: MessageSquareQuote,
          },
        ],
      },
      {
        title: "The Profession",
        links: [
          {
            label: "Frameworks",
            ...frameworks,
            description: "Evidence-based methods professional coaches use",
            icon: Layers,
          },
          {
            label: "Training Programs",
            ...trainingPrograms,
            description: "The programs that train and certify coaches",
            icon: GraduationCap,
          },
          {
            label: "Organizations",
            ...ccos,
            description: "The bodies that set coaching standards",
            icon: Building2,
          },
        ],
      },
    ],
    featured: {
      title: "Coaching Intelligence Hub",
      description:
        "The knowledge layer of coaching — guidance, frameworks, programs, and the people behind them.",
      ...coachingIntelligence,
      ctaLabel: "Explore the hub",
    },
  },
  {
    type: "mega",
    label: "For Coaches",
    groups: [
      {
        title: "Get Started",
        links: [
          {
            label: "Join as a Coach",
            ...pageCoach,
            description: "Free verified profile on the marketplace",
            icon: Rocket,
          },
          {
            label: "6-Figure Readiness Score",
            ...readinessScore,
            description: "Free 2-minute check of what you need for $100K",
            icon: Gauge,
          },
          {
            label: "NOVA",
            ...pageNova,
            description: "The AI that builds your coaching business",
            icon: BrainCircuit,
          },
        ],
      },
      {
        title: "Grow Your Practice",
        links: [
          {
            label: "Business Guides",
            ...coachingBusiness,
            description: "Tactical guides for building a practice",
            icon: BookMarked,
          },
          {
            label: "Playbooks",
            ...playbooks,
            description: "Step-by-step plays for common business moves",
            icon: ListChecks,
          },
          {
            label: "Coach Business Builder",
            ...pageCoachBuilder,
            description: "Hands-on human help to grow",
            icon: Wrench,
          },
        ],
      },
      {
        title: "Platform & Partners",
        links: [
          {
            label: "Coaching OS",
            ...pageCoachingOs,
            description: "Clients, sessions, goals, and homework in one place",
            icon: Briefcase,
          },
          {
            label: "Plans & Pricing",
            ...pagePricing,
            description: "Free to start — upgrade when you're ready",
            icon: DollarSign,
          },
          {
            label: "Partner With Us",
            ...pagePartner,
            description: "For coaching organizations and programs",
            icon: Building2,
          },
        ],
      },
    ],
    featured: {
      title: "Meet NOVA",
      description:
        "Diagnose your business, shape your offer, and build the assets that win clients — AI grounded in real coaching expertise.",
      ...pageNova,
      ctaLabel: "Explore NOVA",
    },
  },
];
